import { resolvePath } from "../layout/resolution.js";

export type CustomDisciplineAthleteRecord = Record<
  string,
  string | number | boolean | Record<string, unknown> | null
>;

export type CustomDisciplineFieldLike = {
  id: string;
  key: string;
  type: string;
  options?: ReadonlyArray<{ id: string; value: string }>;
  referenceSource?: string | null;
  referenceIdPath?: string | null;
  referenceLabelPath?: string | null;
};

export type CustomDisciplineValueLike = {
  fieldDefinitionId: string;
  textValue?: string | null;
  numberValue?: string | number | { toNumber?: () => number } | null;
  selectOptionId?: string | null;
};

export type CustomDisciplineReferenceSources = Record<string, readonly unknown[]>;

/** Runtime key for the athlete UUID on custom-discipline list items. */
export const CUSTOM_DISCIPLINE_ATHLETE_ID_KEY = "id";
export const CUSTOM_DISCIPLINE_ATHLETE_GROUP_ID_KEY = "groupId";
export const CUSTOM_DISCIPLINE_ATHLETE_CUSTOM_GROUP_ID_KEY = "customGroupId";
export const CUSTOM_DISCIPLINE_ATHLETE_ORDER_KEY = "order";

function toFiniteNumber(value: unknown): number | null {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (
    value &&
    typeof value === "object" &&
    typeof (value as { toNumber?: unknown }).toNumber === "function"
  ) {
    const parsed = (value as { toNumber: () => number }).toNumber();
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function normalizeResolvedReferenceRecord(
  item: Record<string, unknown>,
): Record<string, unknown> {
  if (item.name != null && String(item.name).trim() !== "") {
    return item;
  }
  const fallbackName = item.nom;
  if (fallbackName == null || String(fallbackName).trim() === "") {
    return item;
  }
  return { ...item, name: String(fallbackName) };
}

function readForeignKeyIdValue(
  value: CustomDisciplineValueLike | undefined,
): string | null {
  if (!value) {
    return null;
  }
  const textValue = value.textValue?.trim();
  if (textValue) {
    return textValue;
  }
  if (value.numberValue == null || value.numberValue === "") {
    return null;
  }
  return String(value.numberValue);
}

function scalarForField(
  field: CustomDisciplineFieldLike,
  value: CustomDisciplineValueLike | undefined,
): string | number | boolean | null {
  if (!value) {
    return null;
  }
  if (field.type === "SELECT") {
    const option = field.options?.find(
      (item) => item.id === value.selectOptionId,
    );
    return option?.value ?? null;
  }
  if (field.type === "NUMBER" || field.type === "LIFT") {
    return toFiniteNumber(value.numberValue);
  }
  if (field.type === "BOOLEAN") {
    if (value.textValue === "true") {
      return "true";
    }
    if (value.textValue === "false") {
      return "false";
    }
    return null;
  }
  return value.textValue ?? null;
}

/**
 * Resolves a runtime FK object from a reference list.
 *
 * IDs are compared as strings because custom-discipline FK values are stored in
 * EAV `textValue`, while target tables can use numeric or string primary keys.
 */
export function resolveReferenceObject(
  source: readonly unknown[] | null | undefined,
  idPath: string | null | undefined,
  idValue: string | null | undefined,
): Record<string, unknown> | null {
  const expected = String(idValue ?? "").trim();
  const path = String(idPath ?? "id").trim() || "id";
  if (!expected || !Array.isArray(source)) {
    return null;
  }
  const match = source.find((item) => {
    const actual = resolvePath(item, path);
    return String(actual ?? "") === expected;
  });
  if (!match || typeof match !== "object") {
    return null;
  }
  return normalizeResolvedReferenceRecord(match as Record<string, unknown>);
}

/**
 * Resolves the display label of a hydrated FK object on an athlete record.
 */
export function resolveForeignKeyLabelFromRecord(
  record: CustomDisciplineAthleteRecord | undefined,
  field: Pick<
    CustomDisciplineFieldLike,
    "key" | "type" | "referenceLabelPath"
  >,
  fallback = "",
): string {
  if (!record || field.type !== "FOREIGN_KEY" || !field.key) {
    return fallback;
  }
  const resolved = record[field.key];
  if (!resolved || typeof resolved !== "object") {
    return fallback;
  }
  const labelPath = field.referenceLabelPath?.trim() || "name";
  const label = resolvePath(resolved, labelPath);
  return label == null || String(label) === "" ? fallback : String(label);
}

/**
 * Builds a runtime athlete record keyed by field `key` from EAV values.
 *
 * BOOLEAN is `"true"` / `"false"`, DATE is `YYYY-MM-DD`, SELECT is the option
 * `value` (not the option id).
 *
 * @param fields - Field definitions (typically ATHLETE scope).
 * @param values - Persisted EAV rows for one athlete (or group).
 * @param options - Optional system fields (athlete UUID).
 * @returns Record consumed as `data.athlete` / list items in `data.athletes`.
 */
export function athleteRecordFromValues(
  fields: readonly CustomDisciplineFieldLike[],
  values: readonly CustomDisciplineValueLike[],
  options?: {
    id?: string | null;
    groupId?: string | null;
    customGroupId?: string | null;
    order?: number | null;
    referenceSources?: CustomDisciplineReferenceSources | null;
  },
): CustomDisciplineAthleteRecord {
  const byFieldId = new Map(
    values.map((value) => [value.fieldDefinitionId, value]),
  );
  const record: CustomDisciplineAthleteRecord = {};
  const athleteId = options?.id?.trim();
  if (athleteId) {
    record[CUSTOM_DISCIPLINE_ATHLETE_ID_KEY] = athleteId;
  }
  const groupId = options?.groupId?.trim();
  if (groupId) {
    record[CUSTOM_DISCIPLINE_ATHLETE_GROUP_ID_KEY] = groupId;
  }
  const customGroupId = options?.customGroupId?.trim();
  if (customGroupId) {
    record[CUSTOM_DISCIPLINE_ATHLETE_CUSTOM_GROUP_ID_KEY] = customGroupId;
  }
  if (typeof options?.order === "number" && Number.isFinite(options.order)) {
    record[CUSTOM_DISCIPLINE_ATHLETE_ORDER_KEY] = options.order;
  }
  for (const field of fields) {
    if (
      field.key === CUSTOM_DISCIPLINE_ATHLETE_ID_KEY ||
      field.key === CUSTOM_DISCIPLINE_ATHLETE_GROUP_ID_KEY ||
      field.key === CUSTOM_DISCIPLINE_ATHLETE_CUSTOM_GROUP_ID_KEY ||
      field.key === CUSTOM_DISCIPLINE_ATHLETE_ORDER_KEY
    ) {
      continue;
    }
    if (!field.key) {
      continue;
    }
    if (field.type === "FOREIGN_KEY") {
      const idValue = readForeignKeyIdValue(byFieldId.get(field.id));
      record[`${field.key}Id`] = idValue;
      record[field.key] = resolveReferenceObject(
        field.referenceSource
          ? options?.referenceSources?.[field.referenceSource]
          : null,
        field.referenceIdPath,
        idValue,
      );
      continue;
    }
    record[field.key] = scalarForField(field, byFieldId.get(field.id));
  }
  return record;
}

export const groupRecordFromValues = athleteRecordFromValues;
